import { useCallback, useEffect, useRef, useState } from "react";
import { probeDownloadable, downloadHlsToMp4, downloadRelayTsToMp4, downloadSubtitleSrt } from "../playback/hls-download.js";

// The download pill on the detail hero (next to "+ MY LIST") and its dialog.
//
// Clicking DOWNLOAD opens a small dialog with the package's REAL options:
//   • Quality — one entry per master-playlist variant (our extractor writes a
//     single variant; the server reports its true pixel height from the init's
//     tkhd, so the list never shows fake 1080p/2160p tiers we can't deliver).
//   • Audio — every rendition the package carries (Tamil/Telugu/Hindi…),
//     defaulting to the package's DEFAULT=YES track. Relayed LuluStream copies
//     carry one muxed track, so they say so instead of offering a picker.
//   • Subtitles — a checkbox (only when the title has VTT tracks) plus a
//     language select; the picked WebVTT is fetched and saved converted to
//     .srt next to the video.
//
// The server decides what is downloadable (see /api/hosted/download-info):
// our own CORS-open Cloudflare packages are fetched directly, LuluStream
// copies come through /api/hosted/media and are transmuxed TS → MP4. Host
// embeds (Playmate/Streamtape/Vidking) get no button — their bytes live inside
// someone else's player and never reach us.

export default function DownloadButton({ type, tmdbId, title }) {
  const [info, setInfo] = useState(null); // probe result
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [run, setRun] = useState(null); // { percent, label, phase } | null
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setHidden(false);
    setInfo(null);
    probeDownloadable(type, tmdbId, ctrl.signal)
      .then((d) => {
        if (cancelled) return;
        if (!d.downloadable) setHidden(true);
        else setInfo(d);
      })
      .catch(() => {
        if (!cancelled) setHidden(true);
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [type, tmdbId]);

  // Safe to close at any time: while a download runs the dialog is already
  // closed (the handoff below), and the hero pill / inline bar own cancel.
  const closeDialog = useCallback(() => {
    setOpen(false);
    setError(null);
  }, []);

  const start = useCallback(
    async ({ variantIndex, audioIndex, subtitle }) => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setError(null);
      setRun({ percent: 0, label: "Preparing…", phase: "init" });
      try {
        // Two shapes: our own fMP4 packages are fetched directly, relayed
        // LuluStream TS copies go through /api/hosted/media and get remuxed.
        const runDownload = info.source === "relay" ? downloadRelayTsToMp4 : downloadHlsToMp4;
        const r = await runDownload({
          masterUrl: info.masterUrl,
          title: info.title || title,
          variantIndex,
          audioIndex,
          signal: ctrl.signal,
          onProgress: (p) => {
            if (p.phase === "segments") {
              setRun({ phase: "segments", percent: p.percent, label: `Downloading ${(p.percent * 100).toFixed(0)}% · ${fmtBytes(p.bytes)}` });
            } else if (p.phase === "finalizing") {
              setRun({ phase: "finalizing", percent: 1, label: "Finishing…" });
            }
          },
        });
        let subNote = "";
        if (subtitle) {
          setRun({ phase: "subs", percent: 1, label: "Fetching subtitles…" });
          try {
            const s = await downloadSubtitleSrt(subtitle, ctrl.signal);
            saveTextFile(s.srt, s.filename);
            subNote = " + subtitles";
          } catch (e) {
            subNote = " (subtitles failed: " + (e?.message || "error") + ")";
          }
        }
        setRun({ phase: "done", percent: 1, label: `Saved ${fmtBytes(r.bytes)}${subNote}` });
        setTimeout(() => {
          setRun(null);
          abortRef.current = null;
          setOpen(false);
        }, 4000);
      } catch (e) {
        abortRef.current = null;
        setRun(null);
        if (e?.name !== "AbortError") setError(e?.message || "Download failed");
        setOpen(true); // back to the options — cancel must never brick the dialog
      }
    },
    [info, title],
  );

  const cancel = () => abortRef.current?.abort();

  if (hidden) return null;
  if (!info) return <button className="btn btn-ghost dl-btn" disabled>⬇ …</button>;

  const busy = !!run && run.phase !== "done";
  const progress = run && run.phase !== "done" ? (
    <div className="dl-progress" role="progressbar" aria-valuenow={Math.round(run.percent * 100)}>
      <div className="dl-progress-bar" style={{ width: `${Math.max(3, run.percent * 100)}%` }} />
      <span className="dl-progress-label">{run.label}</span>
    </div>
  ) : null;

  return (
    <>
      <button
        className={`btn btn-ghost dl-btn${busy ? " downloading" : ""}`}
        onClick={() => (busy ? cancel() : setOpen(true))}
        title={busy ? "Click to cancel" : "Download options"}
      >
        {busy ? "⏹ CANCEL" : "⬇ DOWNLOAD"}
      </button>
      {progress && (
        <div className="dl-inline-progress" onClick={cancel} title="Click to cancel">
          {progress}
        </div>
      )}
      {run?.phase === "done" && <div className="dl-inline-progress done">{run.label}</div>}
      {open && (
        <DownloadDialog
          info={info}
          fallbackTitle={title}
          onPick={start}
          onClose={closeDialog}
          error={error}
        />
      )}
    </>
  );
}

function DownloadDialog({ info, fallbackTitle, onPick, onClose, error }) {
  const [variantIndex, setVariantIndex] = useState(0);
  const [picked, setPicked] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedImport, setCopiedImport] = useState(false);
  // The subtitle checkbox and both language selects are uncontrolled and read
  // from the DOM at click time: a click on the checkbox and on Download
  // processed in the same task could otherwise hand the downloader a stale
  // controlled value. `wantSubs` state only reveals the language select.
  const wantSubsRef = useRef(null);
  const subRef = useRef(null);
  const [wantSubs, setWantSubs] = useState(false);

  // The dialog shows quality + audio options from the probe. Audio rendition
  // selection happens against the master playlist, which the probe already
  // parsed server-side — but the AUDIO list itself comes from the master at
  // download time. To show language names in the dialog, the server probe
  // would need another parse; instead re-fetch the master here (one small
  // request, cached by the CDN).
  const audioRef = useRef(null);
  const [audioOptions, setAudioOptions] = useState([]);
  const [defaultAudio, setDefaultAudio] = useState(0);
  useEffect(() => {
    // The master playlist is tiny and CDN-cached; parse it here so the dialog
    // can name the audio languages (the server probe reports quality only).
    let cancelled = false;
    fetch(info.masterUrl)
      .then((r) => r.text())
      .then((t) => {
        if (cancelled) return;
        const opts = [];
        let def = 0;
        for (const line of t.split(/\r?\n/)) {
          if (!line.startsWith("#EXT-X-MEDIA:")) continue;
          const get = (k) => {
            const m = new RegExp(`${k}="([^"]*)"`).exec(line) || new RegExp(`${k}=([^,]*)`).exec(line);
            return m ? m[1] : "";
          };
          if ((get("TYPE") || "").toUpperCase() !== "AUDIO" || !get("URI")) continue;
          if (get("DEFAULT").toUpperCase() === "YES") def = opts.length;
          opts.push({ name: get("NAME") || "Audio", language: get("LANGUAGE") || "" });
        }
        setAudioOptions(opts);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [info.masterUrl]);

  const fmtQ = (v) => {
    if (v.height) return `${v.height}p`;
    if (v.width && v.height) return `${v.width}×${v.height}`;
    return `${Math.round((v.bandwidth || 0) / 1000)} kbps`;
  };

  // The shareable address of this copy. Both sources are HLS playlists, so the
  // link is a manifest (VLC, ffmpeg, yt-dlp and most download managers accept
  // one) rather than a single .mp4 — that file is assembled by this page.
  //
  // Prefer the permanent link: it names only the title, and the server re-mints
  // the source's signed token on every request, so a link copied today still
  // works next week. `masterUrl` is the raw source URL, which carries their
  // ~8-hour signature and is only good for this session.
  const base = typeof location !== "undefined" ? location.href : "https://poku-tv-web.netlify.app/";
  const shareable = info.permanentUrl || info.masterUrl;
  const linkUrl = shareable ? new URL(shareable, base).href : "";
  // The same manifest under an `.m3u8` name, for a video host's REMOTE UPLOAD.
  // Their importers go by the file ending: handed the `.mkv` download link they
  // parse the body as a video file and fail the job outright, while `.m3u8`
  // they sniff as HLS and follow. Which one you want depends on the reader, so
  // the dialog offers both.
  const importUrl = info.importUrl ? new URL(info.importUrl, base).href : "";
  const copyTo = async (url, mark) => {
    if (!url) return;
    if (!(await copyText(url))) return;
    mark(true);
    setTimeout(() => mark(false), 2500);
  };
  const copyLink = () => copyTo(linkUrl, setCopied);
  const copyImportLink = () => copyTo(importUrl, setCopiedImport);

  return (
    <div className="dl-overlay" onClick={onClose} role="presentation">
      <div className="dl-dialog" role="dialog" aria-modal="true" aria-label={`Download ${info.title || fallbackTitle}`} onClick={(e) => e.stopPropagation()}>
        <h3 className="dl-title">Download {info.title || fallbackTitle}</h3>

        {info.variants.length > 1 ? (
          <div className="dl-group">
            <span className="dl-group-label">Quality</span>
            <div className="dl-options">
              {info.variants.map((v, i) => (
                <label key={i} className={`dl-option${i === variantIndex ? " selected" : ""}`}>
                  <input type="radio" name="dlq" checked={i === variantIndex} onChange={() => setVariantIndex(i)} />
                  {fmtQ(v)}
                  <span className="dl-option-sub">{Math.round((v.bandwidth || 0) / 1000)} kbps</span>
                </label>
              ))}
            </div>
          </div>
        ) : info.variants.length === 1 ? (
          <p className="dl-single-note">
            Quality: {fmtQ(info.variants[0])} (the only copy in the library)
            {info.source === "relay" && info.variants[0].durationSeconds
              ? ` · ${Math.round(info.variants[0].durationSeconds / 60)} min`
              : ""}
          </p>
        ) : null}

        {info.source === "relay" && (
          <p className="dl-single-note">
            LuluStream copy — fetched through PoKu and saved as .mp4
            {info.variants[0]?.segmentCount ? ` (${info.variants[0].segmentCount} parts)` : ""}.
          </p>
        )}

        {info.muxed && (
          <p className="dl-single-note">
            Audio: {info.audio ? `${info.audio} — ` : ""}single track, muxed with the video
          </p>
        )}

        {!info.muxed && audioOptions.length > 0 && (
          <div className="dl-group">
            <span className="dl-group-label">Audio language</span>
            <select ref={audioRef} className="dl-select" defaultValue={defaultAudio} key={defaultAudio}>
              {audioOptions.map((o, i) => (
                <option key={i} value={i}>
                  {o.name}{o.language ? ` (${o.language})` : ""}{i === defaultAudio ? " — default" : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {info.subtitles.length > 0 && (
          <div className="dl-group">
            <label className="dl-check">
              <input ref={wantSubsRef} type="checkbox" checked={wantSubs} onChange={(e) => setWantSubs(e.target.checked)} />
              Also download subtitles (.srt)
            </label>
            {wantSubs && (
              <select ref={subRef} className="dl-select" defaultValue={0}>
                {info.subtitles.map((s, i) => (
                  <option key={s.id || i} value={i}>
                    {s.label || s.lang || "Subtitles"}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        {info.subtitles.length === 0 && (
          <p className="dl-single-note">No subtitles available for this title.</p>
        )}

        {(linkUrl || importUrl) && (
          <div className="dl-copy">
            {linkUrl && (
              <button type="button" className={`btn btn-ghost btn-sm dl-copy-btn${copied ? " copied" : ""}`} onClick={copyLink} title={linkUrl}>
                {copied ? "✓ Copied" : "🔗 Copy download link"}
              </button>
            )}
            {importUrl && (
              <button type="button" className={`btn btn-ghost btn-sm dl-copy-btn${copiedImport ? " copied" : ""}`} onClick={copyImportLink} title={importUrl}>
                {copiedImport ? "✓ Copied" : "📥 Copy host-import link"}
              </button>
            )}
            <span className="dl-copy-note">
              {info.permanentUrl
                ? `Permanent PoKu link — never expires. Both are the same HLS playlist; the download link names it .mkv/.mp4 for a player or downloader, the import link names it .m3u8 because a video host's remote upload rejects a playlist wearing a video extension.`
                : "Direct link to the stream (.m3u8). The source's access key expires after a few hours."}
            </span>
          </div>
        )}

        {error && <p className="dl-error">{error}</p>}

        <div className="dl-actions">
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={picked}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            disabled={picked}
            onClick={() => {
              setPicked(true);
              // Hand off to the inline progress view; the dialog closes so the
              // bar (and the CANCEL pill on the hero) are actually visible.
              setTimeout(() => {
                setPicked(false);
                onClose();
              }, 0);
              const wantSubs = wantSubsRef.current?.checked ?? false;
              const subIndex = Number(subRef.current?.value ?? 0);
              onPick({
                variantIndex,
                audioIndex: audioRef.current ? Number(audioRef.current.value) : defaultAudio,
                subtitle: wantSubs && info.subtitles[subIndex] ? info.subtitles[subIndex] : null,
              });
            }}
          >
            ⬇ Download
          </button>
        </div>
      </div>
    </div>
  );
}

// Clipboard with a fallback for browsers that gate navigator.clipboard
// (http, older Safari, missing permission). Returns false when both fail.
async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the textarea trick */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

function saveTextFile(text, filename) {
  const blob = new Blob([text], { type: "application/x-subrip" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
}

function fmtBytes(n) {
  if (!Number.isFinite(n)) return "";
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(0)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}
